import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { heroSlideService } from "../../services";
import FallbackImg from "../../assets/img/heroSlider/1.jpg";

// Video de fondo. Se puede sobreescribir con REACT_APP_HERO_VIDEO_URL
// (por ejemplo una URL de Cloudinary/S3). Por defecto: public/video/hero.mp4
const VIDEO_SRC = process.env.REACT_APP_HERO_VIDEO_URL || "/video/hero.mp4";

const CACHE_KEY = "heroSlides";

const fallbackSlide = {
  title: "Una Escapada Ideal",
  subtitle: "Hotel Boutique",
  imageUrl: FallbackImg,
  buttonText: "Ver Nuestras Suites",
  buttonLink: "#rooms",
};

const getCachedSlides = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {}
  return null;
};

// Cada cuánto rotan los textos cuando no hay video (fallback a imagen)
const TEXT_INTERVAL_MS = 5000;
// Duración del fundido entre textos
const FADE_MS = 450;

// Scroll suave a un ancla, descontando header + barra de reserva fija
export const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const header = document.getElementById("header")?.offsetHeight || 0;
  const bar = document.getElementById("booking-bar")?.offsetHeight || 0;
  const top = el.getBoundingClientRect().top + window.scrollY - header - bar - 16;
  window.scrollTo({ top, behavior: "smooth" });
};

const HeroVideo = () => {
  // Título / subtítulo / botón vienen de los slides activos del dashboard.
  // Los textos rotan sincronizados con el video (se reparte la duración
  // del video entre los slides) o cada TEXT_INTERVAL_MS si no hay video.
  const [slides, setSlides] = useState(() => getCachedSlides() || [fallbackSlide]);
  const [index, setIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef(null);
  const indexRef = useRef(0);
  const switchingRef = useRef(false);

  const slide = slides[index] || slides[0];

  const goTo = useCallback((next) => {
    if (next === indexRef.current || switchingRef.current) return;
    switchingRef.current = true;
    setFading(true);
    setTimeout(() => {
      indexRef.current = next;
      setIndex(next);
      setFading(false);
      switchingRef.current = false;
    }, FADE_MS);
  }, []);

  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    const load = async () => {
      try {
        const data = await heroSlideService.getSlides();
        if (data && data.length > 0) {
          const fresh = JSON.stringify(data);
          if (fresh !== localStorage.getItem(CACHE_KEY)) {
            localStorage.setItem(CACHE_KEY, fresh);
            setSlides(data);
            indexRef.current = 0;
            setIndex(0);
          }
        }
      } catch (error) {
        console.warn("Error cargando slides del hero, usando cache/fallback:", error);
      }
    };
    load();
  }, []);

  // iOS/Safari: asegurar muted antes de autoplay
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, [videoFailed]);

  const showVideo = !videoFailed && !reducedMotion;

  // Sin video: rotar textos por tiempo
  useEffect(() => {
    if (showVideo || slides.length < 2) return;
    const id = setInterval(() => {
      goTo((indexRef.current + 1) % slides.length);
    }, TEXT_INTERVAL_MS);
    return () => clearInterval(id);
  }, [showVideo, slides.length, goTo]);

  // Con video: elegir el texto según el momento del video
  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration || slides.length < 2) return;
    const segment = v.duration / slides.length;
    const next = Math.min(slides.length - 1, Math.floor(v.currentTime / segment));
    goTo(next);
  };

  return (
    <section
      className="relative z-0 w-full h-screen overflow-hidden bg-primary"
      style={{ height: "100svh" }}
    >
      {/* Poster / fallback si no hay video */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${slide.imageUrl || FallbackImg})` }}
        role="img"
        aria-label={slide.title}
      />

      {showVideo && (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
          style={{ opacity: videoReady ? 1 : 0 }}
          src={VIDEO_SRC}
          poster={slide.imageUrl || FallbackImg}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onCanPlay={() => setVideoReady(true)}
          onTimeUpdate={handleTimeUpdate}
          onError={() => setVideoFailed(true)}
          aria-hidden="true"
        />
      )}

      {/* Oscurecido + degradé inferior para que la barra de reserva se lea bien */}
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/60 to-transparent" />

      {/* Contenido */}
      <div
        className="relative z-10 h-full flex flex-col justify-center items-center text-white text-center px-4 pb-24 lg:pb-32 transition-opacity ease-out"
        style={{ opacity: fading ? 0 : 1, transitionDuration: `${FADE_MS}ms` }}
        key={index}
      >
        {slide.subtitle && (
          <div className="uppercase font-tertiary tracking-[6px] mb-5 animate-heroFadeUp">
            {slide.subtitle}
          </div>
        )}
        <h1
          className="text-[36px] font-primary uppercase tracking-[2px] max-w-[920px] lg:text-[72px] leading-tight mb-8 animate-heroFadeUp"
          style={{ animationDelay: "150ms" }}
        >
          {slide.title}
        </h1>
        {slide.buttonText && slide.buttonLink && (
          <div className="animate-heroFadeUp" style={{ animationDelay: "300ms" }}>
            {slide.buttonLink.startsWith("/") ? (
              <Link to={slide.buttonLink} className="hover:opacity-85 transition">
                <button className="btn btn-lg btn-primary mx-auto">{slide.buttonText}</button>
              </Link>
            ) : (
              <a
                href={slide.buttonLink}
                className="hover:opacity-85 transition"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(slide.buttonLink.replace("#", ""));
                }}
              >
                <button className="btn btn-lg btn-primary mx-auto">{slide.buttonText}</button>
              </a>
            )}
          </div>
        )}
      </div>

      {/* Indicador de scroll */}
      <button
        type="button"
        aria-label="Bajar"
        onClick={() => scrollToSection("welcome")}
        className="hidden lg:flex absolute bottom-36 left-1/2 -translate-x-1/2 z-10 flex-col items-center text-white/80 hover:text-white transition"
      >
        <span className="relative block w-[26px] h-[44px] rounded-full border-[1.5px] border-white/80">
          <span className="absolute left-1/2 top-2 -ml-[2px] block w-1 h-2 rounded-full bg-white animate-heroWheel" />
        </span>
        <span className="font-tertiary text-[13px] tracking-wide mt-2.5">Desliza para descubrir</span>
      </button>
    </section>
  );
};

export default HeroVideo;
